import { Modal } from "bootstrap";
import { EligibilityAction } from "../../Models/enums";

export default class LicenceEligibilityCheck {
    private modalElement: HTMLDivElement;
    private modal: Modal;
    private questionContainer: HTMLDivElement;
    private questions: HTMLDivElement[];
    private resultContainer: HTMLDivElement;
    private backButton: HTMLButtonElement;
    private currentPage: number = 1;
    private isResultShown: boolean = false;
    private isResetting: boolean = false;
    constructor(ModalId: string) {
        this.modalElement = document.getElementById(ModalId) as HTMLDivElement;
        if (!this.modalElement) return;

        this.init();
    }

    private async init() {
        this.setElements();
        this.setEventListeners();
    }

    private setElements() {
        this.modal = new Modal(this.modalElement);
        this.questionContainer = this.modalElement.querySelector<HTMLDivElement>(".question-carousel");
        this.resultContainer = this.modalElement.querySelector<HTMLDivElement>(".result");
        this.backButton = this.modalElement.querySelector<HTMLButtonElement>(".back--btn");
        this.questions = Array.from(this.questionContainer.querySelectorAll<HTMLDivElement>(".question"));
    }

    private setEventListeners() {
        this.modalElement.addEventListener("shown.bs.modal", () => {
            this.moveSlide();
            this.handleFocus();
            this.updateBackButtonState();
        });

        this.modalElement.addEventListener("hide.bs.modal", (e: Event) => {
            if (!this.isResetting) {
                e.preventDefault();
                this.isResetting = true;
                this.modal.hide();
                this.resetResults();
            } else {
                this.isResetting = false;
                return;
            }
        });

        const answerButtons = this.questionContainer.querySelectorAll<HTMLButtonElement>(".button");
        answerButtons?.forEach(button => {
            button.addEventListener("click", this.debounce(this.onAnswerClick.bind(this), 300));
        });

        this.backButton.addEventListener("click", this.debounce(this.onBackButtonClick.bind(this), 300));
        
        const finishedTestButtons = this.modalElement.querySelectorAll<HTMLButtonElement>('.result button.button');
        finishedTestButtons?.forEach(button => button.addEventListener('click', () => this.modal.hide()));
    }

    private onAnswerClick(e: Event) {
        const answer = e.target as HTMLButtonElement;
        const action = Number(answer.getAttribute("data-action"));

        if (action == EligibilityAction.Eligible || action == EligibilityAction.Not_Eligible) {
            const result = answer.getAttribute("data-response");
            this.showResult(result, action);
        } else {
            this.showNextQuestion();
        }
    }

    private showResult(result: string, action: EligibilityAction) {
        this.isResultShown = true;
        this.questionContainer.classList.remove("show");
        this.resultContainer.classList.add("show");

        const resultWrappers = this.resultContainer.querySelectorAll<HTMLElement>(".result-wrapper");
        resultWrappers.forEach(wrapper => wrapper.classList.remove("show"));

        const resultWrapper = this.resultContainer.querySelector<HTMLElement>(
            action === EligibilityAction.Eligible ? ".result-wrapper.eligible" : ".result-wrapper.not-eligible"
        );
        resultWrapper.classList.add("show");

        const resultTextEl = resultWrapper.querySelector<HTMLDivElement>(".result-text");
        resultTextEl.innerHTML = this.decodeHtml(result);

        if (resultTextEl) {
            resultTextEl.setAttribute("tabindex", "0");
            this.handleFocus(resultTextEl);
        }

        this.updateBackButtonState();
    }

    private moveSlide() {
        const translateXValue: number = -(this.currentPage - 1) * 100;

        this.questions.forEach((question, index) => {
            question.style.transform = `translateX(${translateXValue}%)`;

            if (index === this.currentPage - 1) {
                question.classList.add("show");
            } else {
                question.classList.remove("show");
            }
        });

        // Wait until transition is finished, then... (this addresses timing issues for mouse and keyboard events)
        this.questions[this.currentPage - 1].addEventListener('transitionend', () => {
            this.handleFocus();
            this.updateBackButtonState();
        }, { once: true });
    }

    private showNextQuestion() {
        this.currentPage++;
        this.moveSlide();
    }

    private showPreviousQuestion() {
        this.currentPage--;
        this.moveSlide();
    }

    private updateBackButtonState() {
        if (this.currentPage === 1 && !this.isResultShown) {
            this.backButton.classList.add("disabled");
            this.backButton.setAttribute("disabled", "true");
        } else {
            this.backButton.classList.remove("disabled");
            this.backButton.removeAttribute("disabled");
        }
    }

    private onBackButtonClick() {
        if (this.isResultShown) {
            this.isResultShown = false;

            this.resultContainer.classList.remove("show");
            this.questionContainer.classList.add("show");

            this.updateBackButtonState();
        } else if (this.currentPage > 1) {
            this.showPreviousQuestion();
        }
    }

    private handleFocus(element?: HTMLElement) {
        const currentQuestionsPage = this.questions[this.currentPage - 1];
        const closeModalButton = this.modalElement.querySelector<HTMLButtonElement>('.close-modal');

        if (this.isResultShown === false && currentQuestionsPage) {
            const currentFocusableElements = Array.from(currentQuestionsPage.querySelectorAll<HTMLElement>('.answers button'));
            currentFocusableElements.push(closeModalButton);

            if (currentFocusableElements.length > 0) {
                currentFocusableElements[0].focus();
            }
        }

        if (this.isResultShown === true && element) {
            element.focus();
        }
    }
    
    private resetResults() {
        this.currentPage = 1;
        this.isResultShown = false;
        this.questionContainer.classList.add("show");
        this.resultContainer.classList.remove("show");
        this.moveSlide();
    }

    // Debounce included to address clicks during slide transition
    private debounce(func: Function, wait: number) {
        let timeout: ReturnType<typeof setTimeout>;
        return function (this: any, ...args: any[]) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    // Restores Html.Encode() string to HTML
    private decodeHtml(html: string): string {
        let txt = document.createElement("textarea");
        txt.innerHTML = html;
        let decodedHtml = txt.value;

        let parser = new DOMParser();
        let parsedDocument = parser.parseFromString(decodedHtml, "text/html");
        return parsedDocument.body.innerHTML;
    }
}
